import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Users, Plus, Trash2, Crown, UserPlus } from 'lucide-react';

const ShopStaff = () => {
  const { shopId } = useParams();
  const { api } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchStaff = useCallback(async () => {
    try {
      const res = await api.get('/shops/' + shopId + '/staff');
      setStaff(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Fonctionnalit\u00e9 r\u00e9serv\u00e9e au plan PREMIUM");
      }
    } finally { setLoading(false); }
  }, [api, shopId]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const handleAdd = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error('Remplissez tous les champs');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/shops/' + shopId + '/staff', form);
      setStaff(prev => [...prev, res.data]);
      toast.success("Membre ajout\u00e9 \u00e0 l'\u00e9quipe");
      setShowForm(false);
      setForm({ name: '', email: '', password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  const handleRemove = async (staffId, name) => {
    if (!window.confirm("Retirer " + name + " de l'\u00e9quipe ?")) return;
    try {
      await api.delete('/shops/' + shopId + '/staff/' + staffId);
      setStaff(prev => prev.filter(s => s.id !== staffId));
      toast.success("Membre retir\u00e9");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold font-['Manrope']">{"\u00c9quipe"}</h2>
          <p className="text-sm text-muted-foreground">{"G\u00e9rez les membres de votre \u00e9quipe"}</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="rounded-full" data-testid="add-staff-btn">
          <UserPlus className="w-4 h-4 mr-1.5" /> Ajouter un membre
        </Button>
      </div>

      {showForm && (
        <Card data-testid="add-staff-form">
          <CardHeader><CardTitle className="text-base">Nouveau membre</CardTitle></CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <div>
                <Label>Nom</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Nom complet" data-testid="staff-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="email@exemple.com" data-testid="staff-email" />
              </div>
              <div>
                <Label>Mot de passe</Label>
                <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Mot de passe" data-testid="staff-password" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAdd} disabled={submitting} data-testid="staff-submit">
                {submitting ? "Ajout..." : "Ajouter"}
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          {staff.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground mb-2">{"Aucun membre dans l'\u00e9quipe"}</p>
              <p className="text-sm text-muted-foreground">{"Ajoutez des membres pour qu'ils puissent g\u00e9rer votre boutique"}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {staff.map(member => (
                <div key={member.id} className="flex items-center justify-between p-4 rounded-xl border" data-testid={"staff-member-" + member.id}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{member.name}</p>
                      <p className="text-xs text-muted-foreground">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">Staff</Badge>
                    <Button variant="ghost" size="icon" onClick={() => handleRemove(member.id, member.name)} className="text-destructive hover:text-destructive" data-testid={"remove-staff-" + member.id}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="p-4 rounded-xl border border-dashed bg-muted/30">
        <div className="flex items-start gap-3">
          <Crown className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-sm">{"Acc\u00e8s des membres Staff"}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {"Les membres Staff peuvent g\u00e9rer les produits, cat\u00e9gories, banni\u00e8res et le constructeur de page. Ils n'ont pas acc\u00e8s aux param\u00e8tres, \u00e0 la facturation ni \u00e0 la gestion de l'\u00e9quipe."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopStaff;
